import { useState, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import AircraftSilhouette from '@/components/AircraftSilhouette';

export default function Login() {
  const { login, loginWithPasskey } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-night-900 bg-night-radial text-slate-100 lg:grid lg:grid-cols-2">
      {/* ── Hero panel ───────────────────────────────────────────── */}
      <div className="relative hidden overflow-hidden lg:block">
        <div className="absolute inset-0 bg-grid-lines [background-size:44px_44px] opacity-25" />

        {/* Meteorite-livery studio render — white backdrop feathered into the dark
            theme via a radial mask (no cutout needed). */}
        <img
          src="/aircraft-hero.png"
          alt="Cirrus SR22T G7+ in Meteorite livery"
          className="pointer-events-none absolute left-1/2 top-0 w-[118%] max-w-none -translate-x-1/2 select-none"
          style={{
            WebkitMaskImage:
              'radial-gradient(ellipse 60% 62% at 50% 42%, #000 52%, transparent 78%)',
            maskImage:
              'radial-gradient(ellipse 60% 62% at 50% 42%, #000 52%, transparent 78%)',
          }}
        />
        {/* Darken the lower area so the caption stays readable */}
        <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-night-950 via-night-950/85 to-transparent" />

        <div className="relative flex h-full flex-col p-12">
          <div className="flex items-center gap-3">
            <AircraftSilhouette className="h-8 w-8 text-avio" />
            <span className="ident text-sm text-slate-300">FlightTrack</span>
          </div>

          <div className="flex-1" />

          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-300 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-gradient-to-br from-slate-300 to-slate-600 ring-1 ring-white/20" />
              Meteorite livery
            </div>
            <p className="ident text-avio text-sm">N313G</p>
            <h2 className="mt-3 text-5xl font-bold leading-tight tracking-tight text-white">
              Cirrus SR22T
              <span className="block text-avio">G7+</span>
            </h2>
            <p className="mt-4 max-w-md text-slate-300/80">
              Flight logging, ADS-B tracking, and IRS-ready tax compliance —
              for one very well-flown airplane.
            </p>
          </div>

          <div className="mt-8 flex gap-8 text-xs text-slate-400">
            <span><span className="text-slate-200">315</span> HP · TSIO-550-K</span>
            <span><span className="text-slate-200">FL250</span> ceiling</span>
            <span><span className="text-slate-200">CAPS</span> equipped</span>
          </div>
        </div>
      </div>

      {/* ── Auth panel ───────────────────────────────────────────── */}
      <div className="flex min-h-screen items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <AircraftSilhouette className="h-9 w-9 text-avio" />
            <div>
              <p className="ident text-xs text-avio">N313G</p>
              <p className="text-lg font-semibold text-white">FlightTrack</p>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-white">Sign in</h1>
          <p className="mt-1 text-sm text-slate-400">Access the N313G flight deck.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            {error && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                {error}
              </div>
            )}

            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-400">
                Email
              </label>
              <input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-night-800/70 px-3 py-2.5 text-white placeholder-slate-500 outline-none transition focus:border-avio/60 focus:ring-2 focus:ring-avio/30"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-400">
                Password
              </label>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-night-800/70 px-3 py-2.5 text-white placeholder-slate-500 outline-none transition focus:border-avio/60 focus:ring-2 focus:ring-avio/30"
                placeholder="••••••••"
              />
            </div>

            <button type="submit" disabled={busy} className="btn-primary w-full">
              {busy ? 'Signing in…' : 'Sign in'}
            </button>

            <div className="flex items-center gap-3 py-1 text-xs text-slate-600">
              <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
            </div>

            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true); setError('');
                try { await loginWithPasskey(); navigate(from, { replace: true }); }
                catch (err) { setError(err instanceof Error ? err.message : 'Passkey sign-in failed'); }
                finally { setBusy(false); }
              }}
              className="btn-ghost w-full"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                <circle cx="9" cy="8" r="4" /><path d="M15 8a3 3 0 013 3v0M14.5 12.5l1 1 2-2M9 14c-3 0-5 2-5 5v1h8" />
              </svg>
              Sign in with a passkey
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-slate-600">
            FlightTrack · authorized access only
          </p>
        </div>
      </div>
    </div>
  );
}
